import { useQuery } from '@tanstack/react-query'
import SingleDropdown from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import React, { ReactElement } from 'react'
import { useSelector } from 'react-redux'
import { useTranslate } from 'src/localization/Trns'
import type { CountryFieldModel } from 'src/models/country.models'
import type { CountryListResponse } from 'src/models/country.models'
import {
  countriesQueries,
  toCountryListModelMap,
  toCountryOptions,
} from 'src/queries/countries/countries.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'

import style from './BillingAddressFieldset.style'
import {
  BillingAddressErrors,
  BillingAddressField,
  billingAddressFieldsForCountry,
  BillingAddressValues,
  clearUnusedBillingAddressFields,
} from './billingAddressRules'

/**
 * Module scope, not inline: React Query only reuses a previous select result
 * when `options.select` is reference-identical, so an inline arrow would
 * re-derive both shapes on every render.
 */
const toCountryFieldsetData = (countries: CountryListResponse[]) => ({
  options: toCountryOptions(countries),
  byIso: toCountryListModelMap(countries),
})

export type BillingAddressFieldsetProps = {
  legend: string
  /** Prefix for the field test ids so two address blocks never collide. */
  idPrefix: string
  value: BillingAddressValues
  onChange: (next: BillingAddressValues) => void
  errors?: BillingAddressErrors
  /** Renders every control read-only, for a view-without-edit-permission form. */
  readOnly?: boolean
}

/**
 * A country-aware billing address block.
 *
 * Which fields render, whether each is required, its label, its placeholder,
 * and its format are all read from the shared country catalogue
 * (`countriesQueries` → `countrysMap[iso].fields`) — the same source
 * `AddressForm` and `KybAddressFieldset` already use, so the app tells one story
 * about addresses. Nothing about US address shape is hardcoded here.
 *
 * That matters beyond tidiness: the Card Management billing-contact contract
 * makes `line2`, `province`, and `postal` nullable *specifically because* their
 * requirements vary by country, and the server applies country rules before
 * accepting an update. A fixed Street/City/State/ZIP form would send shapes the
 * server rejects, with no field on screen to explain the failure.
 *
 * FIELD LABELS COME FROM THE CATALOGUE, NOT THE DESIGN — decided deliberately,
 * do not "correct" them. The rcn-card-greenfield prototype labels the US shape
 * "Street address" / "Apt, suite, etc." / "ZIP", while the catalogue serves
 * "Billing Address 1" / "Billing Address 2" / "ZIP code". The designer wrote
 * those against a US mock without the catalogue in view, and hardcoding them
 * would put US phrasing on every country — GB would read "ZIP" — which breaks
 * the design's own "Fields reformat per country" promise. Andrew confirmed
 * 2026-08-10 that the catalogue wins; the design labels are the thing to
 * update.
 *
 * Presentational and routing-free: validation lives in `billingAddressRules`
 * so the owning form can run it on submit and pass results back as `errors`.
 */
export default function BillingAddressFieldset(
  props: BillingAddressFieldsetProps,
): ReactElement {
  const {
    legend,
    idPrefix,
    value,
    onChange,
    errors = {},
    readOnly = false,
  } = props
  const translate = useTranslate()
  const locale = useSelector(selectNullSafeLocaleFromState)

  // One observer, not two. Both shapes come from the same cached response, so
  // a single module-scope select derives them together — React Query reuses
  // the result while the data is unchanged, which two separate selects on the
  // same key cannot do.
  const { data: countries } = useQuery({
    ...countriesQueries.list(locale),
    select: toCountryFieldsetData,
  })
  const countryOptions = countries?.options ?? []
  const countriesByIso = countries?.byIso ?? {}

  const fields = billingAddressFieldsForCountry(countriesByIso, value.country)

  const setField = (field: BillingAddressField, next: string): void =>
    onChange({ ...value, [field]: next })

  const setCountry = (nextCountry: string): void => {
    // Clear against the NEW country's rules, so a previous country's postal
    // code cannot ride along in a complete-set replace.
    const nextFields = billingAddressFieldsForCountry(
      countriesByIso,
      nextCountry,
    )
    onChange(
      clearUnusedBillingAddressFields(
        { ...value, country: nextCountry },
        nextFields,
      ),
    )
  }

  const dropdownOptions = countryOptions.map((option) => ({
    value: option.value,
    label: option.details.text,
  }))
  const renderField = (field: CountryFieldModel): ReactElement => {
    const name = field.name as BillingAddressField
    const fieldValue = value[name] ?? ''
    // `options` present means the catalogue enumerates the valid values (US
    // states, CA provinces) — a dropdown, not free text, so the user cannot
    // submit a value the server will reject.
    if (field.options !== undefined && field.options.length > 0 && !readOnly) {
      const options = field.options.map((option) => ({
        value: option.code,
        label: option.label,
      }))
      return (
        <SingleDropdown
          key={name}
          // Matches the TextInputs beside it. SingleDropdown extends
          // TextInputProps but defaults to `medium` (52px), which made the
          // State control visibly taller than City and ZIP in the same row.
          variant="small"
          label={field.label}
          requirement={field.optional ? 'optional' : 'required'}
          searchable
          options={options}
          selectedValue={options.find((option) => option.value === fieldValue)}
          onSelect={(option): void => setField(name, option.value)}
          errorText={errors[name]}
          data-testid={`${idPrefix}-${name}`}
        />
      )
    }

    return (
      <TextInput
        key={name}
        variant="small"
        label={field.label}
        requirement={field.optional ? 'optional' : 'required'}
        placeholder={field.example}
        value={fieldValue}
        readOnly={readOnly}
        errorText={errors[name]}
        onChange={(event): void => setField(name, event.target.value)}
        testId={`${idPrefix}-${name}`}
      />
    )
  }

  // City / province / postal share a row where the country actually uses them;
  // the two street lines always span. Built from the resolved field list rather
  // than a fixed layout, so a country without a province simply has fewer.
  const streetFields = fields.filter((field) =>
    ['address1', 'address2'].includes(field.name),
  )
  const localityFields = fields.filter((field) =>
    ['city', 'province', 'postal'].includes(field.name),
  )

  return (
    <fieldset className="BillingAddressFieldset" css={style}>
      <legend className="BillingAddressFieldset-legend">{legend}</legend>
      {/*
        Country first: it decides which fields below exist.

        The SAME `SingleDropdown variant="small" searchable` as the province
        field below, not a native select. The design's dsGap ("billing country
        remains a feature-local native select until a compatible ADS Select
        contract exists", 2026-07-18-rcn-controls-rest) was written before the
        province decision (Richa, 2026-07-27) settled that enumerated address
        fields use the app's dropdown — and a raw <select> sitting directly
        above a SingleDropdown in the same fieldset read as a different control
        set: different label typography, height, focus ring, and no requirement
        marker. Andrew called the mismatch on 2026-08-11; consistency inside one
        form wins over a prototype note the province field already departs from.

        `searchable` matters more here than for province: this list is every
        country, not 50 states.
      */}
      <SingleDropdown
        variant="small"
        label={translate('Country')}
        requirement="required"
        searchable
        // SingleDropdown defaults to clearable with a "(none)" row. Country is
        // required AND decides which address fields exist, so clearing it
        // unmounts every street/city/state/ZIP input and leaves a dead-end form.
        clearable={false}
        noneLabel=""
        disabled={readOnly}
        options={dropdownOptions}
        selectedValue={dropdownOptions.find(
          (option) => option.value === value.country,
        )}
        onSelect={(option): void => setCountry(option.value)}
        errorText={errors.country}
        microcopy={translate('Fields reformat per country.')}
        data-testid={`${idPrefix}-country`}
      />
      {streetFields.map(renderField)}
      {localityFields.length > 0 && (
        <div className="BillingAddressFieldset-row">
          {localityFields.map(renderField)}
        </div>
      )}
    </fieldset>
  )
}
